import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import { getUploadUrl, toUploadPath } from '../utils/uploadUrl';

function AdminDonorsPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    website: '',
    description: '',
    status: 'active'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPartners();
  }, [navigate, search]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('include_inactive', 'true');

      const response = await axios.get(`/api/donors-partners?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setPartners(response.data.donorsPartners || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = toUploadPath(response.data.filename || response.data.path || response.data.imageUrl);
      setFormData(prev => ({ ...prev, image: imageUrl }));
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      image: '',
      website: '',
      description: '',
      status: 'active'
    });
    setFormError('');
    setShowAddModal(true);
  };

  const handleEdit = async (partner) => {
    try {
      const token = getAccessToken();
      const response = await axios.get(`/api/donors-partners/${partner.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const fullPartner = response.data;
      
      setEditingPartner(fullPartner);
      setFormData({
        name: fullPartner.name || '',
        image: fullPartner.image || '',
        website: fullPartner.website || '',
        description: fullPartner.description || '',
        status: fullPartner.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching partner details:', error);
      setEditingPartner(partner);
      setFormData({
        name: partner.name || '',
        image: partner.image || '',
        website: partner.website || '',
        description: partner.description || '',
        status: partner.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const trimmedName = (formData.name || '').trim();

      if (!trimmedName || trimmedName.length === 0) {
        setFormError('Name is required');
        setFormLoading(false);
        return;
      }

      if (!formData.image || !formData.image.trim()) {
        setFormError('Image is required');
        setFormLoading(false);
        return;
      }

      const token = getAccessToken();
      const data = {
        ...formData,
        name: trimmedName,
        website: (formData.website || '').trim() || null,
        description: (formData.description || '').trim() || null
      };

      if (editingPartner) {
        await axios.put(`/api/donors-partners/${editingPartner.id}`, data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Donor/Partner updated successfully!');
      } else {
        await axios.post('/api/donors-partners', data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Donor/Partner created successfully!');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingPartner(null);
      await fetchPartners();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to save donor/partner';
      setFormError(errorMessage);
      
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        navigate('/admin/login');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this donor/partner?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/donors-partners/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      alert('Donor/Partner deleted successfully!');
      fetchPartners();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete donor/partner');
    }
  };

  const filteredPartners = partners.filter(partner =>
    partner.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col">
            <h1 className="h3 mb-0">Donors & Partners</h1>
          </div>
          <div className="col-auto">
            <button className="btn btn-primary" onClick={handleAdd}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Donor/Partner
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="row mb-4">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Partners Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Website</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPartners.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No donors/partners found
                      </td>
                    </tr>
                  ) : (
                    filteredPartners.map((partner) => (
                      <tr key={partner.id}>
                        <td>
                          {partner.image ? (
                            <img
                              src={partner.image}
                              alt={partner.name}
                              style={{ width: '80px', height: '60px', objectFit: 'contain' }}
                              onError={(e) => {
                                e.target.src = '/img/Insurance.jpeg';
                              }}
                            />
                          ) : (
                            <div style={{ width: '80px', height: '60px', backgroundColor: '#ddd' }}></div>
                          )}
                        </td>
                        <td>{partner.name}</td>
                        <td>
                          {partner.website ? (
                            <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                              {partner.website.length > 30 ? `${partner.website.substring(0, 30)}...` : partner.website}
                              <i className="bi bi-box-arrow-up-right ms-1"></i>
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{partner.display_order || 0}</td>
                        <td>
                          <span className={`badge ${partner.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                            {partner.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(partner)}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(partner.id)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        <div className={`modal fade ${showAddModal ? 'show' : ''}`} style={{ display: showAddModal ? 'block' : 'none' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Donor/Partner</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && (
                    <div className="alert alert-danger">{formError}</div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Image *</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      required={!formData.image}
                    />
                    {formData.image && (
                      <div className="mt-2">
                        <img
                          src={getUploadUrl(formData.image)}
                          alt="Preview"
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <small className="text-muted">Upload logo/image for the donor/partner</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Website</label>
                    <input
                      type="url"
                      className="form-control"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="alert alert-info">
                    <small><i className="bi bi-info-circle me-1"></i>Display order is automatically set based on creation order (first created = order 1, second = order 2, etc.)</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading}>
                    {formLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {showAddModal && <div className="modal-backdrop fade show"></div>}

        {/* Edit Modal */}
        <div className={`modal fade ${showEditModal ? 'show' : ''}`} style={{ display: showEditModal ? 'block' : 'none' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Donor/Partner</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && (
                    <div className="alert alert-danger">{formError}</div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Image *</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                    {formData.image && (
                      <div className="mt-2">
                        <img
                          src={getUploadUrl(formData.image)}
                          alt="Preview"
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <small className="text-muted">Upload logo/image for the donor/partner</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Website</label>
                    <input
                      type="url"
                      className="form-control"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="alert alert-info">
                    <small><i className="bi bi-info-circle me-1"></i>Display order is automatically set based on creation order (first created = order 1, second = order 2, etc.)</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading}>
                    {formLoading ? 'Saving...' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {showEditModal && <div className="modal-backdrop fade show"></div>}
      </div>
    </AdminLayout>
  );
}

export default AdminDonorsPartners;
